import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Stethoscope, Loader2, MessageSquarePlus } from 'lucide-react';
import { Card } from '../../components/ui/Card.jsx';
import { useSSEChat } from '../../hooks/useSSEChat.js';
import { ChatPane } from '../../components/IvrAgents/Workspace/ChatPane.jsx';
import { ActionProposalContext } from '../../components/SuperAdmin/ActionProposalCard.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useTenant } from '../../components/TenantContext.jsx';
import { dealerApi } from '../../api/dealerDiagnose.js';
import DealerTicketsPanel from '../../components/Dealer/DealerTicketsPanel.jsx';

/**
 * /dealer/diagnose — 經銷商診斷對話
 *   選一個自己的客服單位（tenant）→ 與診斷 AI 對話（同超管引擎，但純唯讀 + 脫敏）。
 *   送訊息：/ivr-builder/api/dealer/tenants/:hostId/chat（SSE，session=dlr-<uid>-<hostId>）。
 */
export default function DealerDiagnosePage() {
    const { t } = useTranslation('dealer');
    const { user } = useAuth();
    const { hostId: ctxHostId } = useTenant();
    const uid = user?.uid;

    const [tenants, setTenants] = useState([]);
    const [hostId, setHostId] = useState(null);
    const [loading, setLoading] = useState(true);

    // session id 綁「該 tenant 對應的 dealer uid」（後端一致）；dealer 看自己 = 自己 uid，super_admin 預覽 = 該 tenant 的 dealer
    const selected = tenants.find((x) => x.hostId === hostId) || null;
    const sessionUid = selected?.dealerUid || uid;

    useEffect(() => {
        let alive = true;
        dealerApi.myTenants()
            .then((r) => {
                if (!alive) return;
                const list = r?.tenants || [];
                setTenants(list);
                const pick = list.find((x) => x.hostId === ctxHostId) || list[0];
                setHostId(pick?.hostId || null);
            })
            .catch(() => { if (alive) setTenants([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [ctxHostId]);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <Stethoscope size={18} className="text-accent-600" />
                <h1 className="heading-display text-xl font-semibold text-ink-900">{t('title')}</h1>
            </div>
            <p className="text-sm text-ink-500 max-w-2xl">{t('intro')}</p>

            <DealerTicketsPanel />

            <div className="flex items-center gap-2">
                <span className="text-xs text-ink-500">{t('pickTenant')}</span>
                {loading ? (
                    <Loader2 size={14} className="animate-spin text-ink-400" />
                ) : tenants.length === 0 ? (
                    <span className="text-xs text-ink-400 italic">{t('noTenants')}</span>
                ) : (
                    <select
                        value={hostId || ''}
                        onChange={(e) => setHostId(e.target.value)}
                        className="rounded-md border border-cream-300 bg-white px-2.5 py-1 text-sm text-ink-800 focus-visible:outline-2 focus-visible:outline-accent-500"
                    >
                        {tenants.map((tn) => (
                            <option key={tn.hostId} value={tn.hostId}>{tn.displayName}</option>
                        ))}
                    </select>
                )}
            </div>

            {hostId && sessionUid
                ? <DealerChat key={`${sessionUid}-${hostId}`} hostId={hostId} uid={sessionUid} />
                : !loading && tenants.length > 0 && <Card><div className="p-4 text-sm text-ink-400">{t('selectPrompt')}</div></Card>}
        </div>
    );
}

/** 單一 tenant 的診斷對話（key=uid-hostId → 切 tenant 時整個重掛、狀態乾淨）。 */
function DealerChat({ hostId, uid }) {
    const { t } = useTranslation('dealer');
    const chat = useSSEChat(`dlr-${uid}-${hostId}`, {
        sendUrl: `/ivr-builder/api/dealer/tenants/${encodeURIComponent(hostId)}/chat`,
    });

    // 純唯讀：不會有 action-proposal，但提供 benign context 避免 ChatPane 取 null。
    const proposalCtx = useMemo(() => ({ sendMessage: chat.sendMessage, onExecuted: () => {} }), [chat.sendMessage]);

    const [resetting, setResetting] = useState(false);
    const [resetConfirm, setResetConfirm] = useState(false);
    const resetChat = async () => {
        if (!resetConfirm) {
            setResetConfirm(true);
            setTimeout(() => setResetConfirm(false), 4000);
            return;
        }
        setResetConfirm(false);
        setResetting(true);
        try {
            await dealerApi.resetChat(hostId);
            await chat.reload();
        } catch (e) {
            alert(t('newChatFailed', { reason: e.message }));
        } finally {
            setResetting(false);
        }
    };

    return (
        <div className="flex flex-col rounded-lg border border-cream-200 bg-cream-50/40 overflow-hidden h-[calc(100dvh-240px)] min-h-[420px]">
            <div className="px-4 py-2.5 border-b border-cream-200 flex items-center gap-2">
                <Bot size={15} className="text-accent-500" />
                <span className="text-sm font-semibold text-ink-900">{t('chatTitle')}</span>
                <button
                    type="button"
                    onClick={resetChat}
                    disabled={resetting || chat.isStreaming}
                    title={t('newChatHint')}
                    className={`ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors
                        focus-visible:outline-2 focus-visible:outline-accent-500 disabled:opacity-40 disabled:cursor-not-allowed
                        ${resetConfirm ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' : 'border-cream-300 text-ink-600 hover:bg-cream-100'}`}
                >
                    {resetting ? <Loader2 size={13} className="animate-spin" /> : <MessageSquarePlus size={13} />}
                    {resetConfirm ? t('newChatConfirm') : t('newChat')}
                </button>
            </div>
            <ActionProposalContext.Provider value={proposalCtx}>
                <div className="flex-1 min-h-0">
                    <ChatPane
                        chat={chat}
                        emptyTitle={t('emptyTitle')}
                        emptyHint={t('emptyHint')}
                        placeholder={t('inputPlaceholder')}
                    />
                </div>
            </ActionProposalContext.Provider>
        </div>
    );
}
